import React from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';

interface BidsData {
  competitiveBidsObtained: string;
  bid1Description: string;
  bid1Total: string;
  bid2Description: string;
  bid2Total: string;
  bid3Description: string;
  bid3Total: string;
  selectionReason: string;
}

interface BidsFormProps {
  data: BidsData;
  onChange: (data: BidsData) => void;
  disabled?: boolean;
  errors?: { field: string; message: string }[];
}

const BidsForm: React.FC<BidsFormProps> = ({ data, onChange, disabled = false, errors = [] }) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const handleChange = (field: keyof BidsData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  return (
    <Box>

      <FormControl component="fieldset" disabled={disabled} error={!!getFieldError('competitiveBidsObtained')} sx={{ mb: 3 }}>
        <FormLabel component="legend">
          Were competitive bids or other pricing comparisons obtained?
        </FormLabel>
        <RadioGroup
          value={data.competitiveBidsObtained}
          onChange={handleChange('competitiveBidsObtained')}
        >
          <FormControlLabel value="YES" control={<Radio />} label="YES" />
          <FormControlLabel value="NO" control={<Radio />} label="NO" />
        </RadioGroup>
        {getFieldError('competitiveBidsObtained') && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            {getFieldError('competitiveBidsObtained')}
          </Typography>
        )}
      </FormControl>

      {data.competitiveBidsObtained === 'NO' && (
        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Explain why no competitive bids were obtained"
          value={data.selectionReason}
          onChange={handleChange('selectionReason')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('selectionReason')}
          helperText={getFieldError('selectionReason')}
          sx={{ mb: 3 }}
        />
      )}

      {data.competitiveBidsObtained === 'YES' && (
        <>
          <Typography variant="body2" sx={{ mb: 3 }}>
            List each vendor, total cost, and give a description about the bid (quantitative and qualitative assessment):
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bid 1
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={data.bid1Description}
              onChange={handleChange('bid1Description')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid1Description')}
              helperText={getFieldError('bid1Description')}
            />
            <TextField
              sx={{ width: '200px' }}
              label="Bid Total (USD)"
              value={data.bid1Total}
              onChange={handleChange('bid1Total')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid1Total')}
              helperText={getFieldError('bid1Total')}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bid 2
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={data.bid2Description}
              onChange={handleChange('bid2Description')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid2Description')}
              helperText={getFieldError('bid2Description')}
            />
            <TextField
              sx={{ width: '200px' }}
              label="Bid Total (USD)"
              value={data.bid2Total}
              onChange={handleChange('bid2Total')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid2Total')}
              helperText={getFieldError('bid2Total')}
            />
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bid 3
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={data.bid3Description}
              onChange={handleChange('bid3Description')}
              variant="outlined"
              disabled={disabled}
            />
            <TextField
              sx={{ width: '200px' }}
              label="Bid Total (USD)"
              value={data.bid3Total}
              onChange={handleChange('bid3Total')}
              variant="outlined"
              disabled={disabled}
            />
          </Box>
        </Box>

            <Alert severity="info">
              NOTE: Please explain why you chose this vendor.
            </Alert>

            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label="Please document why the vendor was selected"
              value={data.selectionReason}
              onChange={handleChange('selectionReason')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('selectionReason')}
              helperText={getFieldError('selectionReason')}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default BidsForm;
